import { PixelIcon } from '@/components/ui';
import { useGame } from '@/contexts/GameContext';
import { puzzles } from '@/game/puzzles';
import { getWorldForLevel } from '@/config/worlds';
import { FONT, FONT_SIZE, SPACING, COLOR, glassPillStyle, statCardStyle } from '@/config/designTokens';
import { motion } from 'motion/react';
import React, { useMemo } from 'react';

interface HighScoreEntry {
  levelIndex: number;
  levelName: string;
  worldColor: string;
  bestScore: number;
  stars: number;
  bestTime: number;
}

interface HighScoresProps {
  mode: 'story' | 'explore';
}

const HighScores: React.FC<HighScoresProps> = ({ mode }) => {
  const { gameState } = useGame();

  // Story mode: top 5 levels by best score + total
  const storyScores = useMemo((): { entries: HighScoreEntry[]; totalScore: number } => {
    const entries: HighScoreEntry[] = [];
    for (const [key, stats] of Object.entries(gameState.levelStats)) {
      const levelIndex = parseInt(key);
      if (isNaN(levelIndex) || levelIndex < 0 || levelIndex >= puzzles.length) continue;
      if (!stats.completed || stats.bestScore <= 0) continue;

      const world = getWorldForLevel(levelIndex);
      entries.push({
        levelIndex,
        levelName: levelIndex === 0 ? 'Tutorial' : `Level ${levelIndex + 1}`,
        worldColor: world.visual?.glow || COLOR.primary,
        bestScore: stats.bestScore,
        stars: stats.stars,
        bestTime: stats.bestTime,
      });
    }

    entries.sort((a, b) => b.bestScore - a.bestScore);
    const totalScore = entries.reduce((sum, e) => sum + e.bestScore, 0);

    return { entries: entries.slice(0, 5), totalScore };
  }, [gameState.levelStats]);

  // Explore mode: highest wave + best score
  const exploreStats = useMemo(() => {
    const highestWave = parseInt(localStorage.getItem('infiniteHighestWave') || '0');
    const bestScore = parseInt(localStorage.getItem('infiniteBestScore') || '0');
    return { highestWave, bestScore };
  }, []);

  const rowStyle = (accentColor: string): React.CSSProperties => ({
    ...statCardStyle(accentColor),
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    border: `1.5px solid color-mix(in srgb, ${accentColor} 25%, transparent)`,
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.sm,
    padding: `${SPACING.sm} ${SPACING.md}`,
  });

  const rankStyle: React.CSSProperties = {
    fontSize: FONT_SIZE.statValue,
    fontWeight: 'bold',
    fontFamily: FONT.heading,
    color: COLOR.starGold,
    minWidth: 28,
    textAlign: 'center',
  };

  const nameStyle: React.CSSProperties = {
    flex: 1,
    fontSize: FONT_SIZE.caption,
    fontFamily: FONT.body,
    color: COLOR.textPrimary,
  };

  const scoreStyle: React.CSSProperties = {
    fontSize: FONT_SIZE.body,
    fontWeight: 'bold',
    fontFamily: FONT.body,
    color: COLOR.score,
  };

  const timeStyle: React.CSSProperties = {
    fontSize: FONT_SIZE.micro,
    fontFamily: FONT.body,
    color: COLOR.textMuted,
  };

  if (mode === 'explore') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={rowStyle(COLOR.gemLight)}
        >
          <PixelIcon name="lightning" size={20} color={COLOR.gemLight} />
          <span style={nameStyle}>Highest Wave</span>
          <span style={{ ...scoreStyle, color: COLOR.gemLight }}>
            {exploreStats.highestWave > 0 ? `Wave ${exploreStats.highestWave}` : '--'}
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          style={rowStyle(COLOR.score)}
        >
          <PixelIcon name="star" size={20} color={COLOR.score} />
          <span style={nameStyle}>Best Score</span>
          <span style={scoreStyle}>
            {exploreStats.bestScore > 0 ? exploreStats.bestScore.toLocaleString() : '--'}
          </span>
        </motion.div>

        {exploreStats.highestWave === 0 && (
          <p style={{
            textAlign: 'center',
            fontSize: FONT_SIZE.caption,
            color: COLOR.textMuted,
            fontFamily: FONT.body,
            marginTop: SPACING.sm,
          }}>
            Play Explore Mode to set your first score!
          </p>
        )}
      </div>
    );
  }

  // Story mode
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
      {/* Total score header */}
      <div style={{
        ...glassPillStyle(),
        justifyContent: 'center',
        gap: SPACING.sm,
        cursor: 'default',
      }}>
        <PixelIcon name="star" size={14} color={COLOR.starGold} />
        <span style={{
          fontSize: FONT_SIZE.caption,
          fontFamily: FONT.body,
          color: COLOR.textSecondary,
        }}>
          Total Score:
        </span>
        <span style={{
          fontSize: FONT_SIZE.body,
          fontWeight: 'bold',
          fontFamily: FONT.body,
          color: COLOR.score,
        }}>
          {storyScores.totalScore.toLocaleString()}
        </span>
      </div>

      {/* Top 5 entries */}
      {storyScores.entries.length > 0 ? (
        storyScores.entries.map((entry, i) => (
          <motion.div
            key={entry.levelIndex}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + i * 0.05 }}
            style={rowStyle(entry.worldColor)}
          >
            <span style={rankStyle}>{i + 1}</span>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <span style={nameStyle}>{entry.levelName}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs }}>
                {[1, 2, 3].map(s => (
                  <PixelIcon
                    key={s}
                    name="star"
                    size={10}
                    color={s <= entry.stars ? COLOR.starGold : 'rgba(255,255,255,0.15)'}
                  />
                ))}
                <span style={timeStyle}>
                  {entry.bestTime < 60
                    ? `${entry.bestTime}s`
                    : `${Math.floor(entry.bestTime / 60)}m ${entry.bestTime % 60}s`}
                </span>
              </div>
            </div>
            <span style={scoreStyle}>{entry.bestScore.toLocaleString()}</span>
          </motion.div>
        ))
      ) : (
        <p style={{
          textAlign: 'center',
          fontSize: FONT_SIZE.caption,
          color: COLOR.textMuted,
          fontFamily: FONT.body,
          marginTop: SPACING.sm,
        }}>
          Complete levels to see your high scores!
        </p>
      )}
    </div>
  );
};

export default HighScores;
